/**
 * Message-ID → conversation pointer document schema and paths.
 *
 * `deriveThreadId` (in the functions `pollInboxLib`) can only recognise a
 * conversation from headers the message itself carries — Gmail's `X-GM-THRID`,
 * or the `References` root. That is enough while every message in a
 * conversation passes through the same mailbox, and not enough as soon as we
 * send a reply through the Trigger Email relay: the relay assigns its own
 * `Message-ID` and forwards neither our `headers` map nor `message.messageId`,
 * so the recipient's reply-to-our-reply references an id that appears nowhere
 * in our data. Its `References` root is the relay's id, `deriveThreadId`
 * derives a brand-new conversation, and one conversation becomes two — in two
 * buckets, since bucket membership is an access boundary.
 *
 * This collection is the missing lookup: one pointer per message whose real
 * RFC-822 `Message-ID` we know, naming the conversation that message belongs
 * to. Ingest builds the candidate ids out of a new message's `In-Reply-To` and
 * `References`, and adopts the conversation of the nearest ancestor it
 * recognises.
 *
 * It is populated from both directions, and both halves are needed:
 *
 * - **Our outbound replies**, keyed on the id the relay assigned, captured
 *   from `delivery.info.messageId` after the send (`captureReplyMessageId` in
 *   `@cip/functions`). This is what lets the *first* follow-up rejoin.
 * - **Every ingested message**, keyed on its own header id, written in the same
 *   create-only transaction as the email document. A second follow-up
 *   references the first follow-up, not our reply, so without this half the
 *   chain breaks again one hop later. Indexing every ingested message
 *   propagates the real RFC parent/child chain, whose transitive closure is
 *   the conversation. It also covers a staff reply typed in the Gmail web UI:
 *   that lands in Sent, is polled with a real `Message-ID`, and so becomes a
 *   recognised ancestor too.
 *
 * Two properties are load-bearing and should survive any future edit:
 *
 * - **It is only ever read by known id** — the same reason its sibling
 *   `threadIndex` needs no Firestore composite index, which matters
 *   concretely because this repo creates indexes by hand (see
 *   `src/tooling/firebase-config/README.md`). The alternative
 *   considered here was a collection-group query over the email documents,
 *   which would have thrown `FAILED_PRECONDITION` inside the IMAP fetch loop
 *   with no deploy mechanism to fix it. A change that turns this into a query
 *   is a signal to reconsider, not to add an index.
 * - **Key derivation lives in {@link getMessageThreadIndexDocumentPath}**,
 *   which sanitizes the raw header value itself. The writer and the reader are
 *   different functions in different files; if either derived the key on its
 *   own, a drift between them would silently produce a lookup that can never
 *   hit.
 *
 * Server-written only, and read only by the admin SDK, which bypasses rules —
 * so the deny-by-default rules already cover it and there is no client-SDK
 * mirror of this module.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';

/** Maximum length of a derived Firestore document id (well under the 1500-byte cap). */
const MAX_DOC_ID_LENGTH = 512;

/**
 * Sanitizes an RFC-822 `Message-ID` into a Firestore-safe document id.
 *
 * Strips surrounding angle brackets, replaces any character outside a
 * conservative allowlist (`A–Z a–z 0–9 . _ - @ +`) with `_`, trims leading
 * dots (Firestore forbids ids `.`/`..`), and caps the length. Ids that would
 * collide with Firestore's reserved `__*__` pattern are prefixed.
 *
 * Lives here rather than in `@cip/functions` because both the id of an email
 * document and the key of this index are derived from a `Message-ID`, and a
 * key derived two different ways is a lookup that never hits. Ingest re-exports
 * it from `pollInboxLib`.
 *
 * Note that the allowlist is lossy: two distinct `Message-ID`s can sanitize to
 * the same id. That is already true of the email documents keyed the same way,
 * and the collision would have to be between two ids in the same mailbox's
 * ancestry to matter.
 *
 * @param raw - The raw `Message-ID` header value (with or without brackets).
 * @returns A Firestore-safe id, or `null` when `raw` sanitizes to empty.
 * @throws Never (string manipulation only).
 */
export function sanitizeMessageId(raw: string): string | null {
  const unbracketed = raw.trim().replace(/^<|>$/g, '');
  let cleaned = unbracketed.replace(/[^A-Za-z0-9._\-@+]/g, '_');
  // Firestore document ids may not be "." or ".." nor begin with a dot in a
  // way that produces those; strip leading dots defensively.
  cleaned = cleaned.replace(/^\.+/, '');
  if (cleaned.length === 0) {
    return null;
  }
  if (cleaned.length > MAX_DOC_ID_LENGTH) {
    cleaned = cleaned.slice(0, MAX_DOC_ID_LENGTH);
  }
  // Avoid the reserved `__.*__` id pattern.
  if (/^__.*__$/.test(cleaned)) {
    cleaned = `id_${cleaned}`;
  }
  return cleaned;
}

/**
 * Base schema for a pointer document at `messageThreadIndex/{sanitizedId}`.
 *
 * Answers one question — "which conversation does the message with this
 * `Message-ID` belong to?" — and carries the bucket and email document
 * alongside so a reader can reach the message itself without a query.
 */
export const MessageThreadIndexDocumentSchema = z
  .object({
    /**
     * Conversation the message belongs to: the same id the message's own email
     * document and its thread document are keyed on.
     */
    threadId: z.string(),
    /**
     * Bucket the message's email document was in when the entry was written —
     * a course id, or the bulk bucket id (`UNCATEGORIZED_BUCKET_ID` in
     * `../reportBucket`).
     *
     * A best-effort back-reference, not an invariant: ingest writes every
     * message into the bulk bucket and `processEmail` may then re-route the
     * document to a course bucket, which does not rewrite this entry.
     * `threadId` is the field to trust — a conversation id never moves.
     */
    bucketId: z.string(),
    /**
     * Document id of the message's email document under
     * `reports/{bucketId}/emails`, subject to the same staleness caveat as
     * `bucketId`.
     */
    emailId: z.string(),
    /**
     * Server time the entry was written. Entries are create-only, so today
     * this is always the time it was first written; the field exists so a
     * future re-pointing pass can record when it moved.
     */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link MessageThreadIndexDocumentSchema}. */
export type MessageThreadIndexDocument = z.infer<
  typeof MessageThreadIndexDocumentSchema
>;

/** Top-level collection mapping a real `Message-ID` to its conversation. */
export const MESSAGE_THREAD_INDEX_COLLECTION = 'messageThreadIndex' as const;

/**
 * Message-thread-index collection path.
 *
 * @returns Firestore collection path `messageThreadIndex`.
 * @throws Never (string construction only).
 */
export function getMessageThreadIndexCollectionPath(): string {
  return MESSAGE_THREAD_INDEX_COLLECTION;
}

/**
 * Single message → conversation pointer document path.
 *
 * Sanitizes `messageId` itself, deliberately: this is the one place a raw
 * header value is turned into a key, so the writer and the reader cannot
 * disagree about how. Pass the header value as received, brackets and all.
 *
 * @param args.messageId - Raw RFC-822 `Message-ID` header value, with or
 *   without its angle brackets. A single id only — an `In-Reply-To` carrying
 *   several must be split first, since the whole string would sanitize into one
 *   unmatchable key.
 * @returns Firestore doc path `messageThreadIndex/{sanitizedId}`, or `null`
 *   when `messageId` sanitizes to nothing usable and so cannot be a key.
 * @throws Never (string construction only).
 */
export function getMessageThreadIndexDocumentPath({
  messageId,
}: {
  messageId: string;
}): string | null {
  const sanitized = sanitizeMessageId(messageId);
  if (sanitized === null) {
    return null;
  }
  return `${getMessageThreadIndexCollectionPath()}/${sanitized}`;
}
